<script setup>
import { onBeforeMount, ref, useTemplateRef, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWorkflows } from '@/services/workflowService.js'
import { getCampaign, getCampaignTemplates, getCampaignTypes } from '@/services/campaignService.js'
import { sanitizeInputAmount } from '@/libs/utils.js'

import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import TranslationProvider from '@/provider/translations.js'

library.add(faTrash)

const props = defineProps(['updatePageTitle'])
const router = useRouter()
const route = useRoute()
const cId = route.params.id

const loadingForm = ref(false)
const template = ref([])
const selectedTemplate = ref({})
const selectedWorkflow = ref(null)
const selectedFixed = ref(false)
const hideFields = ref(true)
const templateError = ref({
  enabled: false,
  message: 'Seleziona un template'
})
const networkError = ref({
  enabled: false,
  message: 'Errore nel salvataggio dei dati , riprova più tardi'
})
const workflowError = ref({
  enabled: false,
  message: 'Seleziona un workflow'
})
const titleError = ref({
  enabled: false,
  message: 'Titolo obbligatorio'
})
const dateStartError = ref({
  enabled: false,
  message: 'Data di inizio obbligatoria'
})
const dateEndError = ref({
  enabled: false,
  message: 'La data di fine deve essere maggiore della data odierna'
})
const workflow = ref([])
const attributes = ref()
const newAttributeKey = ref('')
const newAttributeValue = ref('')
const title = ref('')
const types = ref([])
let selectedType = defineModel('selectedType')
const dateFrom = defineModel('date-from')
const dateTo = defineModel('date-to')
const dateStart = defineModel('dateStart')
const dateEnd = defineModel('dateEnd')
const amount = defineModel('amount')
const noGecContractsFound = ref(false)
const card = useTemplateRef('card')

const campaign = ref(null)
const canView = ref(false)

onBeforeMount(async () => {
  canView.value = route.meta?.permissions?.canView ?? false

  if (!canView.value) {
    return
  }

  loadingForm.value = true

  const campaignTemplates = await getCampaignTemplates()
  template.value = campaignTemplates.data

  workflow.value = await getWorkflows('campaign')

  types.value = await getCampaignTypes()
  selectedType.value = types.value[0].key

  loadingForm.value = false
})

const detailCampaign = async campaignId => {
  try {
    campaign.value = await getCampaign(campaignId, {poll: 1})

    selectedTemplate.value = {
      reference: campaign.value.pollTemplateReference,
      revisionNumber: campaign.value.pollTemplateRevisionNumber,
      title: campaign.value?.poll.title
    }

    selectedWorkflow.value = campaign.value.workflow
    selectedFixed.value = campaign.value.fixed
    title.value = campaign.value.title
    dateStart.value = campaign.value.dateStart ? campaign.value.dateStart.split('T')[0] : null
    dateEnd.value = campaign.value.dateEnd ? campaign.value.dateEnd.split('T')[0] : null

    if (!campaign.value.attributes) {
      campaign.value.attributes = {}
    } else {
      attributes.value = campaign.value.attributes
      if (campaign.value.attributes['startDate']) {
        dateFrom.value = campaign.value.attributes['startDate']
      }
      if (campaign.value.attributes['endDate']) {
        dateTo.value = campaign.value.attributes['endDate']
      }
      if (campaign.value.attributes['amount']) {
        amount.value = campaign.value.attributes['amount']
      }
    }
  } catch (error) {
    window.scrollTo(0, 0)
  }
}

watchEffect(async () => {
  if (cId && canView.value) {
    await detailCampaign(cId)
    let pageTitle = 'Modifica campagna - ' + campaign.value.title
    props.updatePageTitle(pageTitle)
  }
})
</script>

<template>
  <div class="container-fluid px-0">
    <div class="card" ref="card">
      <div class="card-body" v-if="loadingForm">
        <div class="d-flex justify-content-center">
          <div class="spinner-grow" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
      <div class="card-body scrollable-body m-0" v-else ref="card-body">
        <div
          v-if="networkError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ networkError.message }}
        </div>

        <div
          v-if="workflowError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ workflowError.message }}
        </div>

        <div
          v-if="templateError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ templateError.message }}
        </div>

        <div
          v-if="titleError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ titleError.message }}
        </div>

        <div
          v-if="dateStartError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ dateStartError.message }}
        </div>

        <div
          v-if="dateEndError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ dateEndError.message }}
        </div>

        <div
          v-if="noGecContractsFound"
          class="alert alert-warning mt-2"
          role="alert"
        >
          Nessun contratto trovato
        </div>

        <div class="mb-3 col-12 px-0">
          <label for="title">Titolo *</label>
          <input
            type="text"
            class="form-control"
            v-model="title"
            placeholder="Titolo"
            required
            disabled
          />
        </div>

        <div class="mb-3">
          <label for="template">Template *</label>
          <v-select
            disabled
            :options="template"
            label="title"
            :reduce="
              temp => ({
                reference: temp.reference,
                revisionNumber: temp.revisionNumber
              })
            "
            v-model="selectedTemplate"
            placeholder="Seleziona un template"
          >
            <template #no-options>
              <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
            </template>
          </v-select>
        </div>
        <div class="mb-3">
          <label for="workflow">Workflow *</label>
          <v-select
            disabled
            v-model="selectedWorkflow"
            :options="workflow"
            :reduce="perm => perm.key"
            required
            placeholder="Seleziona un workflow"
          >
            <template #no-options>
              <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
            </template>
          </v-select>
        </div>
        <div class="container-fluid mb-2 px-0">
          <div class="row">
            <div class="col-12 col-lg-6">
              <label for="dateStart">Data inizio campagna *</label>
              <input
                id="dateStart"
                type="date"
                class="form-control"
                v-model="dateStart"
                @click="showPicker"
                readonly
                disabled
                placeholder="Data dal"
              />
            </div>
            <div class="col-12 col-lg-6">
              <label for="dateEnd">Data fine campagna</label>
              <input
                id="dateEnd"
                type="date"
                class="form-control"
                v-model="dateEnd"
                @click="showPicker"
                readonly
                disabled
                placeholder="Data al"
              />
            </div>
          </div>
        </div>
        <div class="mb-3 container-fluid px-0">
          <div class="row">
            <div class="col-12 pt-3">
              <h6>
                {{ TranslationProvider.getTrans('app.campaign_form.contract_dates')
                }}</h6>
            </div>
            <div class="col-12 col-lg-4">
              <label for="dateFrom">Data dal *</label>
              <input
                disabled
                id="dateFrom"
                type="date"
                class="form-control"
                v-model="dateFrom"
                @click="showPicker"
              />
            </div>
            <div class="col-12 col-lg-4 mt-3 mt-lg-0">
              <label for="dateFrom">Data al *</label>
              <input
                disabled
                id="dateTo"
                type="date"
                class="form-control"
                v-model="dateTo"
                @click="showPicker"
              />
            </div>
            <div class="col-12 col-lg-4 mt-3 mt-lg-0">
              <label for="dateFrom">Importo maggiore o uguale a *</label>
              <input
                disabled
                id="dateTo"
                type="text"
                class="form-control"
                v-model="amount"
                placeholder="Importo maggiore o uguale a"
                inputmode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                @input="amount = sanitizeInputAmount(amount)"
              />
            </div>
          </div>
        </div>
        <div class="mb-3" v-if="!hideFields">
          <label for="">Attributi</label>
          <div v-for="(value, key) in attributes" :key="key" class="mb-2">
            <div class="input-group">
              <span class="input-group-text">{{ key }}</span>
              <input
                type="text"
                class="form-control"
                v-model="attributes[key]"
                placeholder="Modifica valore"
                disabled
              />
              <button disabled class="btn btn-danger">
                <font-awesome-icon icon=" fa-trash" />
              </button>
            </div>
          </div>

          <div class="input-group mt-3">
            <input
              type="text"
              class="form-control"
              v-model="newAttributeKey"
              placeholder="Nuovo attributo"
              disabled
            />
            <input
              type="text"
              class="form-control"
              v-model="newAttributeValue"
              placeholder="Valore"
              disabled
            />
            <button class="btn btn-secondary">
              Aggiungi Attributo
            </button>
          </div>
        </div>

        <div class="row" v-if="!hideFields">
          <div class="mb-3 col-12 col-lg-6 ps-lg-2">
            <label for="type">Tipologia</label>
            <v-select
              v-model="selectedType"
              :options="types"
              :reduce="type => type.key"
              placeholder="Seleziona una Tipologia"
              label="label"
              disabled=""
            >
              <template #no-options>
                <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
              </template>
            </v-select>
          </div>
        </div>

        <div class="form-check mb-3" v-if="!hideFields">
          <input
            class="form-check-input"
            type="checkbox"
            id="flexCheckDefault"
            v-model="selectedFixed"
            disabled
          />
          <label class="form-check-label" for="fixed"> Fissa </label>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <ButtonTornaAllaLista
        class="me-auto"
        @click="
          router.back()
        "
      >
      </ButtonTornaAllaLista>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fa-trash {
  width: 0.9rem;
}

.vs__dropdown-toggle {
  font-size: 1rem;
  padding: 0.25rem 0.10rem !important;
}
</style>
